/**
 * QuickActions — the Command Center's small view actions, beside the
 * Posture · Operations · History tabs: commit the accepted changes (only
 * when some wait), download the baseline report (not while nothing is
 * assessed), and refresh the page data.
 *
 * Carbon small buttons. The view's one Soft Green primary action lives in
 * the brief, so these are ghost buttons. The provenance line now sits under
 * the verdict; `lastAssessedAt` still renders it here when a caller passes it.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'
import { Button, IconClipboardCheck, IconDownload, Tag } from '@/components/carbon'
import LastAssessed from '@/components/common/LastAssessed'

interface Props {
  selectedTenantId?: string
  pendingProcessingCount: number
  onRefresh?: () => void
  isRefreshing?: boolean
  /** ISO timestamp of the most-recent full assessment across all posture signals. Omit to hide the provenance here. */
  lastAssessedAt?: string | null
  /** Nothing assessed yet (the page's no-data signal): hides the baseline report, which has no baseline to report. */
  noData?: boolean
}

export default function QuickActions({
  selectedTenantId,
  pendingProcessingCount,
  onRefresh,
  isRefreshing = false,
  lastAssessedAt,
  noData = false,
}: Props) {
  const navigate = useNavigate()
  const [isDownloading, setIsDownloading] = useState(false)

  const handleViewPendingProcessing = () => {
    navigate('/configuration-drift?status=Approved')
  }

  const handleDownloadBaseline = async () => {
    if (!selectedTenantId || isDownloading) return

    setIsDownloading(true)
    try {
      const blob = await api.downloadBaselineReport(selectedTenantId, 'html')
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `baseline-report-${selectedTenantId}.html`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (error) {
      console.error('Failed to download baseline:', error)
      toast.error('The baseline report did not download. Try again.')
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-2">
      {/* No cadence: a tenant whose schedule is off is never assessed automatically. */}
      {lastAssessedAt !== undefined && <LastAssessed at={lastAssessedAt} className="mr-2" />}

      {/* Commit changes: only while accepted changes wait to reach the baseline. */}
      {pendingProcessingCount > 0 && (
        <Button kind="ghost" size="sm" icon={<IconClipboardCheck />} onClick={handleViewPendingProcessing}>
          <span className="inline-flex items-center gap-2">
            Commit changes
            <Tag size="sm" className="tabular-nums">
              {pendingProcessingCount}
            </Tag>
          </span>
        </Button>
      )}

      {!noData && (
        <Button
          kind="ghost"
          size="sm"
          icon={<IconDownload />}
          loading={isDownloading}
          disabled={!selectedTenantId}
          onClick={handleDownloadBaseline}
        >
          Download baseline report
        </Button>
      )}

      {onRefresh && (
        <Button kind="ghost" size="sm" loading={isRefreshing} onClick={onRefresh}>
          Refresh
        </Button>
      )}
    </div>
  )
}
